<script lang="ts" setup>
import ArticleContents from '@shared/components/molecules/ArticleContents.vue';
import { normalizeNumberQueryParameter } from '@shared/utils/queryNormalizers';
import { useTransformArticle } from '@shared/composables/useTransformArticle';
import { useImageUrl } from '@shared/composables/useImageUrl';

const route = useRoute('public-article-news-permalink-page');
const router = useRouter();

const articleContentsRef = ref<InstanceType<typeof ArticleContents>>();

definePageMeta({
  middleware: 'pages-public-article-news-permalink',
});

const { createArticleImageUrl } = useImageUrl();
const { isPrArticle, getNewsArticleCategories } = await useCategories();

const { data } = await useGetArticleDetailByPermalink(
  route.params.permalink as string,
  100,
);

const title = computed(() => {
  return data.value!.title;
});

const categories = computed(() => {
  if (!data.value) {
    return [];
  }

  return getNewsArticleCategories(data.value);
});

const imageFile = computed(() => {
  return data.value?.image_file
    ? createArticleImageUrl({ path: data.value.image_file })
    : undefined;
});

const { ogImage } = useOgImage(imageFile.value);

const description = computed(() => {
  return (
    data.value?.lead ||
    'ダイエットプラスの提案する「正しく食べてやせる」ダイエット。おしくてヘルシーなレシピ、ダイエットニュースなど。いままで何をしても痩せなかった人にオススメのダイエット情報を提供。'
  );
});

useSeoMeta({
  title,
  description,
  ogImage,
});

const currentPage = computed(() => {
  return normalizeNumberQueryParameter(route.params.page as string, 1);
});

async function changePage(page: number) {
  await router.push({
    name: 'public-article-news-permalink-page',
    params: {
      page,
      permalink: route.params.permalink,
    },
  });
  articleContentsRef.value?.scrollToContents();
}

const { paginatedContents } = useTransformArticle(data.value!);

if (currentPage.value < 1 || !paginatedContents.value[currentPage.value - 1]) {
  await router.replace({
    name: 'public-article-news-permalink',
    params: {
      permalink: route.params.permalink,
    },
  });
}

async function scrollToSection(page: number, pageSection: number) {
  if (page !== currentPage.value) {
    await router.push({
      name: 'public-article-news-permalink-page',
      params: {
        permalink: route.params.permalink,
        page,
      },
    });
  }
  articleContentsRef.value?.contentAnchorRefs?.[
    pageSection - 1
  ]?.scrollIntoView({
    behavior: 'smooth',
  });
}

async function createPageView() {
  if (!data.value) {
    throw createError({
      statusCode: 404,
      message: 'pageviewの追加に失敗しました',
    });
  }
  await useFetch('/api/kiwi_user/articles/page_view/new', {
    method: 'POST',
    body: {
      article_id: data.value.id,
    },
    server: false,
  });
}

createPageView();
</script>

<template>
  <div>
    <div class="border-gray-300 border rounded mb-5 px-5 py-10">
      <ArticleContents
        v-if="data"
        ref="articleContentsRef"
        :current-page="currentPage"
        :article="data"
        :is-pr="isPrArticle(data)"
        :categories="categories"
        @change-page="changePage"
        @clicked-toc="scrollToSection($event.page, $event.pageSection)"
      >
        <template #before-contents>
          <MoleculesGoogleAd
            id="div-gpt-ad-1534156853557-0"
            target="sp"
            class="w-[336px] h-[280px] mx-auto"
          />
        </template>

        <template #after-contents>
          <div class="hidden md:flex">
            <MoleculesGoogleAd
              id="div-gpt-ad-1534157132108-0"
              target="pc"
              class="w-1/2 h-[250px] shrink-0"
            />
            <MoleculesGoogleAd
              id="div-gpt-ad-1534157160805-0"
              target="pc"
              class="w-1/2 h-[250px] shrink-0"
            />
          </div>
          <MoleculesGoogleAd
            id="div-gpt-ad-1534156907495-0"
            target="sp"
            class="w-[336px] h-[280px] mx-auto"
          />
        </template>

        <template #footer>
          <MoleculesTaxelWidget />
          <MoleculesGoogleAd
            id="div-gpt-ad-1534156947870-0"
            target="sp"
            class="w-[336px] h-[280px] mx-auto mb-5"
          />
          <MoleculesGoogleAd
            id="div-gpt-ad-1534156980762-0"
            target="sp"
            class="w-[336px] h-[280px] mx-auto"
          />
        </template>
      </ArticleContents>
    </div>

    <OrganismsNewsesByCategory class="mb-5" />

    <div class="hidden md:flex">
      <MoleculesGoogleAd
        id="div-gpt-ad-1534157217429-0"
        target="pc"
        class="w-1/2 h-[250px] shrink-0"
      />
      <MoleculesGoogleAd
        id="div-gpt-ad-1534157182002-0"
        target="pc"
        class="w-1/2 h-[250px] shrink-0"
      />
    </div>
  </div>
</template>
